import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaTimesCircle, FaCreditCard } from 'react-icons/fa';

import DnaBackground from '../components/DnaBackground';

const CancelledContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
`;

const CancelledCard = styled(motion.div)`
  background-color: rgba(18, 18, 18, 0.7);
  border-radius: var(--border-radius);
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
`;

const CancelIcon = styled(FaTimesCircle)`
  font-size: 5rem;
  color: #f44336;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 300px;
  margin: 0 auto;
  
  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const ContinueButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem;
  width: 100%;
  border-radius: var(--border-radius);
  cursor: pointer;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
  }
`;

const ReturnButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem;
  width: 100%;
  border-radius: var(--border-radius);
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const PaymentCancelled = () => {
  const navigate = useNavigate();
  
  // Handle navigation to results
  const handleContinueToResults = () => {
    navigate('/results');
  };
  
  // Try payment again
  const handleTryAgain = () => {
    navigate('/results', { state: { tryPaymentAgain: true } });
  };
  
  return (
    <CancelledContainer>
      <DnaBackground />
      <CancelledCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CancelIcon />
        <Title>Payment Cancelled</Title>
        <Description>
          Your payment was cancelled and you have not been charged.
          You can still access your basic results, or try again if you'd like to unlock the premium features.
        </Description>
        <ButtonGroup>
          <ReturnButton 
            onClick={handleContinueToResults}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Return to Results
          </ReturnButton>
          <ContinueButton 
            onClick={handleTryAgain}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Try Again <FaCreditCard />
          </ContinueButton>
        </ButtonGroup>
      </CancelledCard>
    </CancelledContainer>
  );
};

export default PaymentCancelled; 